import { commaListSchema } from "@birdwatch/common";
import { z } from "zod";

import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

/**
 * `validateSearch` for `/office/inbox`.
 *
 * The filters live under the canonical `filters[field][op]` form the rest of the app's
 * lists use, so the shared filter system (`FilterMenu` / `ActiveFilterBar`) can read and
 * write them without this screen owning a second dialect.
 *
 * Lenient types on purpose — a strict `z.enum` on a URL param throws `SearchParamError`
 * and drops the page into the error boundary on any stale link. The component narrows
 * (see [webapp-routing.md]). `maskSearchFragment` is spread so masked-dialog openers
 * fired from this route do not silently no-op, and `notificationSearchFragment` because
 * the office sidebar's notification popup writes `filters[unread]` from any `/office/*`
 * route.
 *
 * The unread filter is keyed `unreadOnly`, NOT `unread`: `filters[unread]` already
 * belongs to that notification popup, and two controls writing one key would fight. The
 * wire field is still `unread` — `buildInboxQuery` does the rename.
 */
export const officeInboxSearchSchema = z.object({
  cid: z.string().optional(),
  search: z.string().optional(),
  /** `place` | `customer` | `none` — default `none`. */
  groupBy: z.string().optional(),
  /**
   * The canonical inbox sort, e.g. `-unread,lastMessageAt`. Default `-lastMessageAt`,
   * written as an absent key. `toInboxSort` narrows it — a string the platform would
   * reject degrades to the default rather than 400ing the list.
   */
  sort: z.string().optional(),
  /** Whose conversations the active queue lists: absent is "Your inbox" (posted in),
   * `members`, `subscribed`, `mentions`, or a team inbox — `onboarding` / `services` /
   * `projects` / `all`. `toInboxKey` narrows it. */
  inbox: z.string().optional(),
  /** `archived` asks the list for the caller's archived conversations; absent is the open
   * queue. A filter of the one list — every other key here applies on both sides of it —
   * kept as its own key rather than a `filters[archived]` chip because the pill row's view
   * chip is its one control, and a funnel field would be a second surface writing it. */
  view: z.string().optional(),
  /** `order` docks the order quick-info sidebar beside the open thread; absent is closed.
   * A string rather than a boolean so a second sidebar can name itself later, and lenient
   * like every other key here — a `z.enum` would throw on a stale link. */
  details: z.string().optional(),
  filters: z
    .object({
      unreadOnly: withOperators(z.string().optional()),
      /** Support only: the household spoke last and nobody has replied. */
      awaitingTeamReply: withOperators(z.string().optional()),
      /** `Order.serviceType` values; orders only. */
      orderType: withOperators(commaListSchema),
      /** The conversation's customer — the order's requester, or a home's lead resident. */
      customerId: withOperators(commaListSchema),
      placeId: withOperators(commaListSchema),
      /** Location (`Market`) ids. */
      locationId: withOperators(commaListSchema),
      ...notificationSearchFragment,
    })
    .optional(),
  ...filterSearchFragment,
  ...maskSearchFragment,
});
